// Dependencies
import React, { ChangeEvent, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context';
import { UserSelector } from '../../components/UserSelector';
import { IAccount, ISortBy, ORDER } from '../../resource';
import {
  Box,
  Button,
  Pagination,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility, Delete } from '@mui/icons-material';

import { IColumn, Table } from '../../components';
import { ROUTES } from '../../contants';
import { PlusIcon } from '../../assets/icons';
import { useToastContext } from '../../components/Toast/toastContext';
import { useAlertContext } from '../../components/Alert/alertContext';
import * as portfolioUploadApi from '../../apis/portfolio-upload.apis';
import { useDebounce } from '../../hooks/useDebounce';

// Utils
import { useUrlParams } from '../../utils/urlParams';

// Portfolio Upload List Page
export const PortfolioUploadPage: React.FC = () => {
  const navigate = useNavigate();
  
  // URL params hook
  const { updateUrlParams, updateUrlParamsAfterApiResponse, getUrlParams, restoreFromStorage } = useUrlParams('portfolioUpload');
  
  // Context
  const { isAdmin, users, account, showDeleted, setShowDeleted, search, setSearch } = useContext(AppContext);
  const debouncedSearch = useDebounce(search);
  const { activeToast } = useToastContext();
  const { activeAlert } = useAlertContext();
  const activeToastRef = useRef(activeToast);

  // Update the ref when it changes
  useEffect(() => {
    activeToastRef.current = activeToast;
  }, [activeToast]);

  const [customer, setCustomer] = useState<IAccount | null | undefined>(null);

  // Handle customer selection change
  const handleCustomerChange = useCallback((newCustomer: IAccount | null) => {
    console.log('Customer changed to:', newCustomer);
    setCustomer(newCustomer);
    setCurrentPage(1); // Reset to first page when customer changes
    updateUrlParams({ 
      customer: newCustomer?.id || undefined,
      page: 1 
    });
  }, [updateUrlParams]);

  const [loading, setLoading] = useState(false);
  const [portfolioUploads, setPortfolioUploads] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);
  const [dataPage, setDataPage] = useState<number>(1);
  const [sort, setSort] = useState<ISortBy>({
    field: 'name',
    order: ORDER.ASC,
  });
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Initialize filters from URL or localStorage
  useEffect(() => {
    console.log('Initializing PortfolioUpload filters...');
    
    // Get current URL parameters
    const urlParams = getUrlParams();
    console.log('Current URL params:', urlParams);
    
    // Get stored filters
    const storedParams = restoreFromStorage();
    console.log('Stored params:', storedParams);
    
    // Determine which parameters to use
    let finalParams;
    
    if (Object.keys(urlParams).length > 0) {
      // URL has parameters - use them
      console.log('Using URL parameters');
      finalParams = urlParams;
    } else if (Object.keys(storedParams).length > 0) {
      // No URL params but stored params exist - use stored params
      console.log('Using stored parameters');
      finalParams = storedParams;
    } else {
      // No stored params - use defaults
      console.log('Using default parameters');
      finalParams = {
        page: 1,
        perPage: 20,
        sort: 'name_asc',
        archived: false,
      };
    }
    
    console.log('Final params to apply:', finalParams);
    
    // Apply the final parameters
    setCurrentPage(finalParams.page || 1);
    setPerPage(finalParams.perPage || 20);
    setSort({
      field: finalParams.sort?.split('_')[0] || 'name',
      order: finalParams.sort?.split('_')[1] === 'desc' ? ORDER.DESC : ORDER.ASC,
    });
    
    if (finalParams.archived !== undefined) {
      setShowDeleted(finalParams.archived);
    }
    
    // Handle search parameter - only restore from URL params, not localStorage
    if (Object.keys(urlParams).length > 0 && urlParams.search !== undefined && urlParams.search !== '') {
      setSearch(urlParams.search);
    }
    
    // Handle customer parameter for admin users
    if (isAdmin && finalParams.customer) {
      const customerUser = users.value.find(user => user.id === finalParams.customer);
      if (customerUser) {
        setCustomer(customerUser);
      }
    }
    
    setIsInitialized(true);
  }, [isAdmin, users.value]);

  // Reusable fetch function
  const fetchPortfolioUploads = useCallback(async (showLoading = true) => {
    // For non-admin users, fetch when account is available
    if (!isAdmin && !account) return;

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    latestRequestId.current += 1;
    const requestId = latestRequestId.current;

    if (showLoading) {
      setLoading(true);
    }

    try {
      const targetCustomer = isAdmin ? customer : account;

      let response;
      if (isAdmin && targetCustomer?.id) {
        response = await portfolioUploadApi.getPortfolioUploads(targetCustomer.id);
      } else {
        // Admin with no user selected → all uploads; customer → own uploads
        response = await portfolioUploadApi.getPortfolioUploads();
      }

      if (response.success) {
        let data = response.data || [];

        // Apply search filter
        if (debouncedSearch) {
          const searchLower = debouncedSearch.toLowerCase();
          data = data.filter((item: any) => {
            const name = (item.name || '').toLowerCase();
            const isinsStr = Array.isArray(item.isins) ? item.isins.join(' ').toLowerCase() : (item.isin || '').toLowerCase();
            const userEmail = item.user?.email?.toLowerCase() || '';
            const userName = `${item.user?.firstName || ''} ${item.user?.lastName || ''}`.toLowerCase();
            return name.includes(searchLower) ||
                   isinsStr.includes(searchLower) ||
                   userEmail.includes(searchLower) ||
                   userName.includes(searchLower);
          });
        }

        // Apply sorting
        data.sort((a: any, b: any) => {
          let aValue: any;
          let bValue: any;
          
          switch (sort.field) {
            case 'name':
              aValue = (a.name || '').toLowerCase();
              bValue = (b.name || '').toLowerCase();
              break;
            case 'isins':
            case 'isin':
              aValue = (Array.isArray(a.isins) ? a.isins.join(' ') : a.isin || '').toLowerCase();
              bValue = (Array.isArray(b.isins) ? b.isins.join(' ') : b.isin || '').toLowerCase();
              break;
            case 'createdAt':
              aValue = new Date(a.createdAt || 0).getTime();
              bValue = new Date(b.createdAt || 0).getTime();
              break;
            case 'user':
              aValue = `${a.user?.firstName || ''} ${a.user?.lastName || ''}`.toLowerCase();
              bValue = `${b.user?.firstName || ''} ${b.user?.lastName || ''}`.toLowerCase();
              break;
            default:
              return 0;
          }
          
          if (sort.order === ORDER.ASC) {
            return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          } else {
            return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
          }
        });

        setTotalCount(data.length);
        
        // Apply pagination
        const startIndex = (currentPage - 1) * perPage;
        const endIndex = startIndex + perPage;
        const paginatedData = data.slice(startIndex, endIndex);
        
        setPortfolioUploads(paginatedData);
        setDataPage(currentPage);
      }
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.message?.includes('canceled') || err?.canceled) {
        return;
      }
      console.log('catch>>>', err);
      setPortfolioUploads([]);
      setTotalCount(0);
      activeToastRef.current({
        type: 'error',
        text: err.message || 'Failed to fetch portfolio uploads',
      });
    } finally {
      if (requestId === latestRequestId.current && showLoading) {
        setLoading(false);
      }
    }
  }, [isAdmin, account, customer, perPage, currentPage, sort, debouncedSearch]);

  // Fetch data when dependencies change
  useEffect(() => {
    if (!isInitialized) return;
    fetchPortfolioUploads();
  }, [isInitialized, fetchPortfolioUploads]);

  // On search changed - update URL after successful API response
  useEffect(() => {
    if (isInitialized && debouncedSearch !== undefined) {
      setTimeout(() => {
        updateUrlParamsAfterApiResponse({ search: debouncedSearch });
      }, 100);
    }
  }, [debouncedSearch, isInitialized]);

  // Calc total page
  const totalPage = useMemo(() => {
    const pages = Math.ceil(totalCount / perPage);
    return pages;
  }, [totalCount, perPage]);

  // Page change handler
  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    console.log('Page changed to:', page);
    setCurrentPage(page);
    updateUrlParams({ page });
  };

  const gotoAddPortfolioUpload = () => {
    navigate(ROUTES.PORTFOLIO_UPLOAD_ADD);
  };

  // Sort handler
  const handleSort = (value: ISortBy) => {
    setSort(value);
    updateUrlParams({ sort: `${value.field}_${value.order}` });
  };

  // Update URL params when showDeleted changes
  useEffect(() => {
    if (isInitialized) {
      setCurrentPage(1);
      updateUrlParams({ page: 1, archived: showDeleted });
    }
  }, [showDeleted, isInitialized]);

  // Delete handler
  const handleDelete = (id: string) => {
    activeAlert({
      type: 'info',
      text: "Are you sure to remove this portfolio upload?",
    }, async () => {
      try {
        await portfolioUploadApi.deletePortfolioUpload(id);
        activeToastRef.current({
          type: 'success',
          text: 'Portfolio upload deleted successfully',
        });
        fetchPortfolioUploads();
      } catch (error: any) {
        activeToastRef.current({
          type: 'error',
          text: error.msg || 'Failed to delete portfolio upload',
        });
      }
    });
  };

  // View handler
  const handleView = (id: string) => {
    navigate(`${ROUTES.PORTFOLIO_UPLOAD}/view/${id}`);
  };

  // Define columns
  const columns: IColumn[] = [
    {
      field: 'index',
      label: '#',
      sortable: false,
      render: (row) => {
        const rowIndex = portfolioUploads.findIndex(item => {
          const itemId = item._id || item.id;
          const rowId = row._id || row.id;
          return String(itemId) === String(rowId);
        });
        return (dataPage - 1) * perPage + rowIndex + 1;
      },
    },
    {
      field: 'name',
      label: 'Name',
    },
    {
      field: 'user',
      label: 'User',
      sortable: true,
      render: (row: any) => {
        const user = row.user;
        if (typeof user === 'object' && user) {
          return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || '-';
        }
        return '-';
      },
    },
    {
      field: 'isins',
      label: 'ISINs',
      sortable: true,
      render: (row: any) => Array.isArray(row.isins) && row.isins.length > 0 ? row.isins.join(', ') : (row.isin ?? ''),
    },
    {
      field: 'actions',
      label: 'Actions',
      sortable: false,
      render: (row) => (
        <Box display="flex">
          <Tooltip title="View">
            {/*@ts-ignore*/}
            <IconButton
              variant="text"
              size="medium"
              onClick={() => handleView(row._id || row.id)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            {/*@ts-ignore*/}
            <IconButton
              variant="text"
              size="medium"
              onClick={() => handleDelete(row._id || row.id)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" marginBottom={20} alignItems="center">
        {isAdmin && <UserSelector value={customer} onChange={handleCustomerChange} />}
        <Button startIcon={<PlusIcon />} onClick={gotoAddPortfolioUpload}>
          Portfolio Upload
        </Button>
      </Box>

      <Stack alignItems="center" spacing={{ xs: 36, md: 66 }}>
        <Table
          columns={columns}
          data={portfolioUploads}
          order={sort.order}
          orderBy={sort.field}
          isLoading={loading}
          onSort={handleSort}
          contentSize={{
            width: {
              xs: 'calc(100vw - 40px)',
              sm: 'calc(100vw - 80px)',
              md: 'calc(100vw - 256px)',
              lg: 'calc(100vw - 376px)',
            },
          }}
        />
        {(totalPage > 1 || totalCount > perPage) && (
          <Pagination 
            page={currentPage} 
            count={totalPage} 
            siblingCount={0} 
            onChange={handlePageChange}
            disabled={loading}
          />
        )}
      </Stack>
    </Box>
  );
};
